import { Alert, Box, Breadcrumbs, Container, Group, Skeleton, Stack, Tabs, Text } from '@mantine/core';
import { notify } from '@/lib/notify';
import { useMedplum } from '@medplum/react-hooks';
import { setPatientIsHighRisk } from '@unify/fhir-model/resources/patient';
import {
  IconAlertCircle,
  IconChartLine,
  IconFolderOpen,
  IconHome,
  IconNotes,
} from '@tabler/icons-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useClients } from '../../contexts/ClientsContext';
import { usePermissions } from '../../contexts/PermissionContext';
import { canRequestServiceFromChart } from '../../lib/chart/chartEpisodeAccess';
import { useEpisodeScopedClientChartBundle } from '../../hooks/useEpisodeScopedClientChartBundle';
import { useRecentlyViewedClients } from '../../contexts/RecentlyViewedClientsContext';
import { usePatientCoverageAndPayment } from '../../hooks/usePatientCoverageAndPayment';
import { getPaymentTypeCode } from '@unify/fhir-model/resources/episode-of-care';
import { ClientHeader } from './components';
import { ClientChartReadinessModal } from './components/ClientChartReadinessModal';
import { TransferModal, type TransferModalContext } from '../caseload/components';
import { TrackerAddEpisodeModal } from './components/TrackerAddEpisodeModal';
import { getClientDisplayName } from '../caseload/data';
import type { CompletedForm, ClientDocument } from './data';
import {
  FormsFilesTab,
  MedicalRecordTab,
  MeasuresTab,
  OverviewTab,
} from './tabs';
import {
  buildChartReadinessGaps,
  isChartReadinessDismissed,
} from './utils/chartReadinessGaps';

/**
 * Client Chart Page
 * Displays comprehensive client information with multiple tabs
 */
/** Stable identity for absent chart lists — tabs sync props into state and must not see a new array each render. */
const EMPTY_LIST: never[] = [];

const VALID_TABS = ['overview', 'chart', 'measures', 'forms'] as const;
const LEGACY_TAB_ALIASES: Record<string, (typeof VALID_TABS)[number]> = {
  'medical-record': 'chart',
};

function normalizeChartTab(tabParam: string | null): (typeof VALID_TABS)[number] {
  if (!tabParam) return 'overview';
  if (LEGACY_TAB_ALIASES[tabParam]) return LEGACY_TAB_ALIASES[tabParam];
  return VALID_TABS.includes(tabParam as (typeof VALID_TABS)[number])
    ? (tabParam as (typeof VALID_TABS)[number])
    : 'overview';
}

export function ClientChartPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const medplum = useMedplum();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab = normalizeChartTab(tabParam);
  const episodeIdParam = searchParams.get('episodeId');

  const { getClientById, caseloadLoading, reloadCaseload } = useClients();
  const { role } = usePermissions();
  const mayAddEpisode = canRequestServiceFromChart(role);
  const client = useMemo(() => (id ? getClientById(id) : undefined), [id, getClientById]);

  const { recordRecentlyViewedClient } = useRecentlyViewedClients();
  const recordedClientIdRef = useRef<string | undefined>();
  useEffect(() => {
    if (!client?.id || recordedClientIdRef.current === client.id) {
      return;
    }
    recordedClientIdRef.current = client.id;
    void recordRecentlyViewedClient(client.id);
  }, [client?.id, recordRecentlyViewedClient]);

  const [addEpisodeModalOpen, setAddEpisodeModalOpen] = useState(false);
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [highRiskOverride, setHighRiskOverride] = useState<boolean | null>(null);
  const [isInsuranceClient, setIsInsuranceClient] = useState(false);
  const [readinessModalOpen, setReadinessModalOpen] = useState(false);

  const {
    coverages,
    paymentMethods,
    loading: billingReadinessLoading,
  } = usePatientCoverageAndPayment(id);

  useEffect(() => {
    if (!id) {
      setIsInsuranceClient(false);
      return;
    }
    let cancelled = false;
    void medplum
      .searchResources('EpisodeOfCare', { patient: `Patient/${id}`, _count: '50' })
      .then((episodes) => {
        if (cancelled) return;
        setIsInsuranceClient(
          episodes.some((episode) => getPaymentTypeCode(episode) === 'insurance'),
        );
      })
      .catch(() => {
        if (!cancelled) setIsInsuranceClient(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, medplum]);

  const isHighRisk = highRiskOverride ?? ((client?.highRiskFlags?.length ?? 0) > 0 || (client?.riskTags?.length ?? 0) > 0);

  // Persists to the Patient `is-high-risk` extension (the same flag intake and the caseload read).
  const handleToggleHighRisk = async () => {
    if (!id) return;
    const next = !isHighRisk;
    setHighRiskOverride(next);
    try {
      const patient = await medplum.readResource('Patient', id);
      await medplum.updateResource(setPatientIsHighRisk(patient, next));
      medplum.invalidateSearches('Patient');
      reloadCaseload();
    } catch (err) {
      setHighRiskOverride(null);
      notify({
        type: 'error',
        title: next ? 'Could not flag high risk' : 'Could not remove high-risk flag',
        error: err,
        message: 'Please try again.',
      });
    }
  };

  // TODO: Resolve practice column for episode list from ServiceRequest.performer Organization
  // See PRD_EPISODES_OF_CARE_FHIR_MAPPING.md
  const { bundle: medplumChart, loading: medplumChartLoading, episodeInteraction, refetch: refetchChartBundle } =
    useEpisodeScopedClientChartBundle(id ?? undefined);

  const chartData = useMemo(() => {
    if (!id) return undefined;
    return medplumChart;
  }, [id, medplumChart]);

  const readinessGaps = useMemo(() => {
    if (!id || !chartData) return [];
    const hasOutstandingIntake =
      (chartData.outstandingForms?.length ?? 0) > 0
      || (chartData.pendingFormTasks?.length ?? 0) > 0;
    return buildChartReadinessGaps({
      clientId: id,
      hasOutstandingIntake,
      isInsuranceClient,
      hasInsuranceCoverage: coverages.some((c) => c.status === 'active' || !c.status),
      hasCardOnFile: paymentMethods.length > 0,
    });
  }, [id, chartData, isInsuranceClient, coverages, paymentMethods]);

  useEffect(() => {
    if (!id || billingReadinessLoading || medplumChartLoading) return;
    if (isChartReadinessDismissed(id)) return;
    if (readinessGaps.length === 0) return;
    setReadinessModalOpen(true);
  }, [id, billingReadinessLoading, medplumChartLoading, readinessGaps]);

  const transferModalContext = useMemo((): TransferModalContext | null => {
    if (!id || !client) return null;
    const episodes = chartData?.episodes ?? [];
    if (episodes.length === 0) return null;
    const authorized = episodeInteraction?.authorizedEpisodeIds;
    const eligible =
      authorized && authorized.size > 0
        ? episodes.filter((episode) => authorized.has(episode.id))
        : episodes;
    if (eligible.length === 0) return null;
    const scoped =
      episodeIdParam != null
        ? eligible.find((episode) => episode.id === episodeIdParam)
        : undefined;
    const episode =
      scoped ??
      eligible.find((e) => e.status === 'active') ??
      eligible[0];
    if (!episode?.id) return null;
    return {
      patientId: id,
      patientDisplay: getClientDisplayName(client),
      episodeOfCareId: episode.id,
      episodeDisplay: episode.name,
      serviceRequestId: '',
      currentProviderDisplay: episode.provider ?? client.primaryProvider ?? client.provider,
      serviceTypeDisplay: episode.episodeType ?? client.clientType,
      episodeOptions: eligible.map((ep) => ({
        value: ep.id,
        label: ep.name,
        currentProviderDisplay: ep.provider ?? client.primaryProvider ?? client.provider,
        serviceTypeDisplay: ep.episodeType ?? client.clientType,
      })),
    };
  }, [id, client, chartData?.episodes, episodeIdParam, episodeInteraction?.authorizedEpisodeIds]);

  const allDischargeSummaries = useMemo(() => {
    const combined = [...(chartData?.dischargeSummaries ?? [])];
    return combined.sort((a, b) => b.dischargeDate.localeCompare(a.dischargeDate));
  }, [chartData]);

  // Only the first load shows the page skeleton. A background refresh (e.g. after a service
  // request is submitted) must keep the page mounted, or open modals lose their state.
  const pageLoading = (caseloadLoading && !client) || (!!id && medplumChartLoading && !medplumChart);

  if (!id) {
    return (
      <Container size="xl" py="xl">
        <Alert icon={<IconAlertCircle size={20} />} title="Error" color="red" variant="light">
          No client ID provided
        </Alert>
      </Container>
    );
  }

  if (!caseloadLoading && !client) {
    return (
      <Container size="xl" py="xl">
        <Alert icon={<IconAlertCircle size={20} />} title="Error" color="red" variant="light">
          Client not found
        </Alert>
      </Container>
    );
  }

  if (pageLoading || !client) {
    return (
      <Container size="xl" py="xl">
        <Stack gap="xl">
          <Skeleton height={20} width={300} />
          <Group gap="md">
            <Skeleton height={56} width={56} radius="md" />
            <Stack gap={4}>
              <Skeleton height={24} width={200} />
              <Skeleton height={16} width={120} />
            </Stack>
          </Group>
          <Box style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}>
            <Group gap="md">
              {['Overview', 'Chart', 'Measures', 'Forms & Files'].map((label) => (
                <Skeleton key={label} height={36} width={100} />
              ))}
            </Group>
          </Box>
          <Stack gap="md">
            <Skeleton height={320} />
            <Skeleton height={200} />
          </Stack>
        </Stack>
      </Container>
    );
  }

  const overviewAppointments = chartData?.appointments ?? [];
  const overviewNotes = chartData?.clinicalNotes ?? [];
  const overviewTasks = chartData?.tasks ?? [];
  const measuresAssessments = chartData?.assessments ?? [];
  const formsCompleted: CompletedForm[] = chartData?.completedForms ?? [];
  const formsDocs: ClientDocument[] = chartData?.documents ?? [];
  const openEpisodes = (chartData?.episodes ?? [])
    .filter((e) => e.status === 'active' || e.status === 'planned' || e.status === 'follow-up' || e.status === 'waitlist');
  const episodeOptions = openEpisodes.map((e) => ({
    id: e.id,
    name: e.name,
    provider: e.provider,
    startDate: e.startDate,
    clientName: client.name,
  }));

  const breadcrumbItems = [
    { title: 'Caseload', href: '/dashboard/caseload' },
    { title: client.name, href: '' },
  ].map((item, index) =>
    item.href ? (
      <Text key={index} component={Link} to={item.href} c="gold.7" size="sm" fw={500}>
        {item.title}
      </Text>
    ) : (
      <Text key={index} size="sm" c="gray.6">
        {item.title}
      </Text>
    ),
  );

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <Breadcrumbs>{breadcrumbItems}</Breadcrumbs>

        <ClientHeader
          client={client}
          onToggleHighRisk={handleToggleHighRisk}
          isHighRisk={isHighRisk}
          onAddEpisode={
            mayAddEpisode ? () => setAddEpisodeModalOpen(true) : undefined
          }
          onTransfer={() => setTransferModalOpen(true)}
        />
        <ClientChartReadinessModal
          opened={readinessModalOpen && readinessGaps.length > 0}
          clientId={id}
          gaps={readinessGaps}
          onClose={() => setReadinessModalOpen(false)}
        />

        <TransferModal
          opened={transferModalOpen}
          onClose={() => setTransferModalOpen(false)}
          context={transferModalContext}
          onSuccess={() => {
            setTransferModalOpen(false);
            refetchChartBundle();
          }}
        />
        {mayAddEpisode && id ? (
          <TrackerAddEpisodeModal
            opened={addEpisodeModalOpen}
            onClose={() => setAddEpisodeModalOpen(false)}
            fixedPatientId={id}
            onCreated={() => {
              refetchChartBundle();
              reloadCaseload();
            }}
          />
        ) : null}

        <Tabs
          value={activeTab}
          onChange={(value) => {
            const next = value ?? 'overview';
            setSearchParams(next === 'overview' ? {} : { tab: next }, { replace: true });
          }}
          styles={{ tab: { transition: 'all 0.2s ease' }, list: { borderBottom: 'none' } }}
          style={{ '--tabs-list-border-size': '2px' } as React.CSSProperties}
        >
          <Tabs.List>
            <Tabs.Tab value="overview" leftSection={<IconHome size={16} />}>
              Overview
            </Tabs.Tab>
            <Tabs.Tab value="chart" leftSection={<IconNotes size={16} />}>
              Chart
            </Tabs.Tab>
            <Tabs.Tab value="measures" leftSection={<IconChartLine size={16} />}>
              Measures
            </Tabs.Tab>
            <Tabs.Tab value="forms" leftSection={<IconFolderOpen size={16} />}>
              Forms & Files
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="overview" mt="md">
            <OverviewTab
              client={client}
              appointments={overviewAppointments}
              episodes={chartData?.episodes ?? []}
              notes={overviewNotes}
              tasks={overviewTasks}
              loading={false}
              episodeInteraction={episodeInteraction}
            />
          </Tabs.Panel>

          <Tabs.Panel value="chart" mt="md">
            <MedicalRecordTab
              key={client.id}
              client={client}
              appointments={chartData?.appointments ?? EMPTY_LIST}
              notes={chartData?.clinicalNotes ?? EMPTY_LIST}
              adminNotes={chartData?.adminNotes ?? EMPTY_LIST}
              episodes={chartData?.episodes ?? EMPTY_LIST}
              dischargeSummaries={allDischargeSummaries}
              historicalRecords={chartData?.historicalRecords ?? EMPTY_LIST}
              initialEpisodeId={episodeIdParam ?? undefined}
              episodeInteraction={episodeInteraction}
              onRefetch={refetchChartBundle}
            />
          </Tabs.Panel>

          <Tabs.Panel value="measures" mt="md">
            <MeasuresTab
              patientId={client.id}
              assessments={measuresAssessments}
              episodes={episodeOptions}
              authorizedEpisodeIds={episodeInteraction?.authorizedEpisodeIds}
              onRefetch={refetchChartBundle}
            />
          </Tabs.Panel>

          <Tabs.Panel value="forms" mt="md">
            <FormsFilesTab
              patientId={client.id}
              completedForms={formsCompleted}
              documents={formsDocs}
              episodes={episodeOptions}
              pendingFormTasks={chartData?.pendingFormTasks}
              onRefetch={refetchChartBundle}
            />
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Container>
  );
}
